import strings from 'owa-strings/owa-common-strings';
import { Button, ButtonType } from 'office-ui-fabric-react/lib/Button';
import { Dialog, DialogFooter, DialogType } from 'office-ui-fabric-react/lib/Dialog';
import { IDropdownOption, Dropdown } from 'office-ui-fabric-react/lib/Dropdown';
import { Label } from 'office-ui-fabric-react/lib/Label';
import * as React from 'react';
import CardItem from 'txp-data/lib/schema/viewSchema/CardItem';
import {
    TxpEventFlagsV2,
    getDialogTextForTxpFlag,
    getDialogTitleForTxpFlag,
    settingsOptions,
} from 'owa-txp-option/lib/utils/getTxpFlagInfo';
import TxpGriffinSettingsPayload from 'owa-calendar-configuration/lib/store/schema/TxpGriffinSettingsPayload';
import getUserSettingsAction from 'owa-calendar-configuration/lib/services/getUserSettingsAction';
import postUserSettingsAction from 'owa-calendar-configuration/lib/services/postUserSettingsAction';
import convertEntityTypeToEventFlag from '../utils/view/convertEntityTypeToEventFlag';
import { isSuccessStatusCode } from 'owa-http-status-codes';
import InfoBar from 'owa-info-bar/lib/components/InfoBar';
import {
    InfoBarMessageSource,
    InfoBarMessageRank,
    InfoBarMessageViewState,
} from 'owa-info-bar/lib/schema/InfoBarMessageViewState';
import txpStrings from 'owa-strings/owa-txp-strings';
import TxpSettingsEntryPoint from 'owa-calendar-configuration/lib/store/schema/TxpSettingsEntryPoint';
import EntityType from 'txp-data/lib/schema/innerSchema/EntityType';
import { reloadTxpSettings } from 'owa-calendar-configuration';
import updateLocalTxpSettings from 'owa-txp-option/lib/utils/updateLocalTxpSettings';

let styles = require('./TxpSettingsDialog.scss');

export interface TxpSettingsDialogProps {
    cardItem: CardItem;
}

interface SettingsDialogState {
    isShown: boolean;
    error: string;
    txpSettings: TxpGriffinSettingsPayload;
}

let counter: number = 0;

export default class TxpSettingsDialog extends React.Component<
    TxpSettingsDialogProps,
    SettingsDialogState
> {
    private discardId = `discard-${counter++}`;
    private settingsOptions: IDropdownOption[];
    private eventType;
    private changedSettings: TxpGriffinSettingsPayload;

    constructor(props: any) {
        super(props);
        this.state = {
            isShown: false,
            error: '',
            txpSettings: null,
        };

        this.changedSettings = {
            CreateEventsFromEmailAsPrivate: null,
            EntityTypeProcessorLevelSettings: {
                FlightReservation: undefined,
                FoodEstablishmentReservation: undefined,
                LodgingReservation: undefined,
                RentalCarReservation: undefined,
                ParcelDelivery: undefined,
                EventReservation: undefined,
                Invoice: undefined,
                ServiceReservation: undefined,
            },
        };

        this.getUserSettings(
            true /* shouldLogDatapoint */,
            TxpSettingsEntryPoint.CardSettings,
            this.props.cardItem.entityType
        );
    }

    render() {
        const { cardItem } = this.props;
        // Default to auto-add if we cannot fetch txpSettings for any reason
        let selectedValue = 2;
        this.eventType = convertEntityTypeToEventFlag(cardItem.entityType);

        if (this.state.txpSettings) {
            selectedValue = this.state.txpSettings.EntityTypeProcessorLevelSettings[this.eventType];
        }

        this.settingsOptions = settingsOptions();

        return (
            <Dialog
                isOpen={this.state.isShown}
                type={DialogType.normal}
                onDismiss={this.onDiscard}
                title={getDialogTitleForTxpFlag(this.eventType as TxpEventFlagsV2)}
                modalProps={{
                    isDarkOverlay: true,
                    className: styles.dialogBox,
                }}>
                {this.state.error && (
                    <InfoBar
                        className={styles.messagebar}
                        textContainerClassName={styles.infobarText}
                        messages={[
                            {
                                key: 'txpSettings',
                                source: InfoBarMessageSource.Txp,
                                rank: InfoBarMessageRank.Error,
                                messageParts: [this.state.error],
                            } as InfoBarMessageViewState,
                        ]}
                    />
                )}
                <div className={styles.dropDownDiv}>
                    <Label>{getDialogTextForTxpFlag(this.eventType as TxpEventFlagsV2)}</Label>
                    <Dropdown
                        selectedKey={selectedValue}
                        options={this.settingsOptions}
                        className={styles.entitySettingDropdown}
                        onChanged={this.updateFlag(this.eventType as TxpEventFlagsV2)}
                    />
                </div>

                <DialogFooter>
                    <Button
                        buttonType={ButtonType.primary}
                        onClick={this.onSave}
                        text={txpStrings.settingsSaveButtonText}
                    />
                    <Button
                        id={this.discardId}
                        buttonType={ButtonType.primary}
                        onClick={this.onDiscard}
                        text={strings.userVoiceFeedbackDiscardButton}
                    />
                </DialogFooter>
            </Dialog>
        );
    }

    /**
     * Function triggered when user clicks discard
     */
    private onDiscard = () => {
        this.setState({
            isShown: false,
        });
    };

    /**
     * Function triggered when user clicks save
     */
    private onSave = () => {
        postUserSettingsAction(this.changedSettings, TxpSettingsEntryPoint.CardSettings).then(
            response => {
                if (isSuccessStatusCode(response.status)) {
                    this.setState({
                        isShown: false,
                    });
                    reloadTxpSettings();
                    updateLocalTxpSettings();
                } else {
                    this.setState({
                        error: 'An error occured while saving your options',
                    });
                    this.getUserSettings();
                }
            }
        );
    };

    /**
     * On change handler for the options dropdown
     */
    private updateFlag = (flag: TxpEventFlagsV2) => (option: IDropdownOption, index?: number) => {
        const selectedValue = this.settingsOptions[index].key as number;
        this.changedSettings.CreateEventsFromEmailAsPrivate = this.state.txpSettings.CreateEventsFromEmailAsPrivate;
        this.changedSettings.EntityTypeProcessorLevelSettings[flag] = selectedValue;
        this.setState({
            txpSettings: this.changedSettings,
        });
    };

    /**
     * Issues the TXP user settings get GWS call and sets the local state
     */
    private async getUserSettings(
        shouldLogDatapoint?: boolean,
        entryPoint?: TxpSettingsEntryPoint,
        entityType?: EntityType
    ) {
        const response = await getUserSettingsAction(shouldLogDatapoint, entryPoint, entityType);
        this.setState({
            isShown: true,
            txpSettings: await response.json(),
        });
    }
}
